import React, { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../hooks/store-hooks";
import { useSuggestedUserQuery } from "../../../store/api/userApi";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import {
  Bookmarks,
  Calender,
  CaretRight,
  CaretUp,
  Cross,
  Topics,
} from "../../../icons";
import SuggestedUser from "../../user/right-bar/suggested-user";
import { Event } from "../../events/types";
import { setSelectedEvent, setStep } from "../../../store/slices/eventSlice";
import { Link } from "react-router-dom";
import { tags, TagWithIcon } from "../main-content/feed/create-feed";
import { useGetEventsQuery } from "../../../store/api/eventApi";
import { getLocalStorage } from "../../../helpers/storage";

const PAGE = {
  HOME: "home",
  BOOKMARKS: "bookmarks",
  ACTIVITY: "activity",
};

interface Topics extends TagWithIcon {
  selected: boolean;
}

const LeftSidebar = ({
  setTopicsQueryString,
}: {
  setTopicsQueryString?: any;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(PAGE.HOME);
  const [showAllTopics, setshowAllTopics] = useState(false);
  const [topics, settopics] = useState<Topics[]>(
    tags.map((tag) => ({ ...tag, selected: false })),
  );
  useGetEventsQuery({ is_active: true, limit: 10, offset: 0 });
  const user = JSON.parse(getLocalStorage("userData") || "{}");
  const userId = user._id || "";

  useEffect(() => {
    if (location.pathname === ROUTES.BOOKMARKS) {
      setPage(PAGE.BOOKMARKS);
    } else if (location.pathname.includes("/activity")) {
      setPage(PAGE.ACTIVITY);
    } else {
      setPage(PAGE.HOME);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (setTopicsQueryString)
      setTopicsQueryString(
        topics
          .filter((topic) => topic.selected)
          .map((topic) => topic.name)
          .join(",")
          .replaceAll(" ", "+"),
      );
  }, [topics]);

  const { isLoading, refetch: refetchSuggestedUsers } = useSuggestedUserQuery(
    { user_id: userId },
    { skip: !userId },
  );

  const { response } = useAppSelector((state) => state.event);

  const { suggestedUser } = useAppSelector((state: any) => state.user || {});

  const handleClick = () => {
    navigate(ROUTES.EVENTS);
  };
  const handleEventClick = (event: Event) => {
    dispatch(setSelectedEvent(event));
    dispatch(setStep(2));
    navigate(ROUTES.EVENTS);
  };

  const handleTopicSelect = (index: number) => {
    const newTopics = [...topics];
    newTopics[index].selected = !newTopics[index].selected;
    newTopics.sort((a, b) =>
      a.selected === b.selected ? 0 : a.selected ? -1 : 1,
    );
    settopics(newTopics);
  };

  return (
    <div className="lg:col-span-3 rounded-lg space-y-6 !h-fit ">
      {/* Upcoming Events Card */}
      {page === PAGE.HOME && (
        <div
          className={`bg-white py-3 px-6 rounded-lg border border-solid border-primary-600 relative ${
            response?.events?.length === 0 && "hidden"
          }`}
        >
          <div className="flex items-center justify-between text-xl font-bold mb-4">
            <div className="flex items-center text-[17px]">
              <div className="mr-2">
                <Calender fillColor="#090909" />
              </div>
              Upcoming events
            </div>
            <span
              className="text-blue-500 cursor-pointer hover:underline text-[13px]"
              onClick={handleClick}
            >
              View All
            </span>
          </div>
          <ul className={`space-y-4 `}>
            {response?.events?.slice(0, 2).map((event, index) => (
              <li
                key={index}
                onClick={() => handleEventClick(event)}
                className="cursor-pointer flex flex-row gap-x-3 items-center "
              >
                <div className="flex flex-col text-gray-500 px-3 py-2 bg-[#F5ECCB] rounded-[5px]">
                  <span className="text-black font-bold text-[15px] text-center">
                    {moment(event.start_time).format("DD").toUpperCase()}
                  </span>
                  <span className="text-[#6C6B6B] font-bold text-[11px]">
                    {moment(event.start_time).format("MMM").toUpperCase()}
                  </span>
                </div>
                <span className="text-[#6C6B6B] justify-center text-[15px]">
                  {event.title}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bookmarks Card */}
      {page !== PAGE.ACTIVITY && (
        <div className="bg-white p-4 rounded-xl border border-solid border-primary-600 relative">
          {page === PAGE.HOME && (
            <>
              <Link to={ROUTES.BOOKMARKS}>
                <div className="text-xl font-semibold mb-2 flex justify-between items-center gap-2 pb-2">
                  <Bookmarks />{" "}
                  <p className="flex-grow px-2 text-[17px] font-semibold">
                    Bookmarks
                  </p>
                  <CaretRight strokeColor="black" width={20} height={20} />
                </div>
              </Link>
              <div className="border-b-[1px] absolute left-0 w-full"></div>
            </>
          )}
          <h3
            className={`text-xl font-semibold mb-4 flex items-center gap-2 ${page === PAGE.HOME ? "mt-4" : "mt-0"}`}
          >
            <Topics /> <p className="text-[17px] font-semibold">Topics</p>
          </h3>
          <ul className="space-y-2 mt-3">
            {topics.map(
              (topic, index) =>
                (index < 5 || showAllTopics) &&
                topic.isFilterable && (
                  <li
                    key={index}
                    onClick={() => handleTopicSelect(index)}
                    className={`flex hover:cursor-pointer rounded-xl-10 items-center justify-between gap-2 py-1`}
                    style={{
                      backgroundColor: topic.selected
                        ? "#F2F2F7"
                        : "transparent",
                    }}
                  >
                    {topic.icon &&
                      React.cloneElement(topic.icon as React.ReactElement, {
                        fillColor: "#090909",
                      })}
                    <p
                      className="text-base font-normal flex-grow"
                      style={{ color: "#090909" }}
                    >
                      {topic.name}
                    </p>
                    {topic.selected && <Cross customClassName="w-3 h-3 mx-2" />}
                  </li>
                ),
            )}
            <li
              onClick={() => setshowAllTopics((prev) => !prev)}
              className="flex items-center gap-2 py-1 hover:cursor-pointer text-secondary-300"
            >
              <div
                className={`transition-all ${showAllTopics ? "" : "rotate-180"}`}
              >
                <CaretUp strokeColor="#007aff" width={16} height={16} />
              </div>
              {`Show ${showAllTopics ? "less" : "more"}`}
            </li>
          </ul>
        </div>
      )}

      {/* Suggested Members Card */}
      {page !== PAGE.BOOKMARKS && (
        <div className="bg-white p-4 rounded-lg border border-solid border-primary-600">
          <h3 className="text-xl font-semibold mb-4">Suggested Members</h3>
          <ul>
            {isLoading ? (
              <li>Loading...</li>
            ) : suggestedUser?.length > 0 ? (
              suggestedUser?.map((user: any) => (
                <li key={user._id} className="flex items-center">
                  <SuggestedUser
                    member={user}
                    refetchSuggestedUser={refetchSuggestedUsers}
                  />
                </li>
              ))
            ) : (
              <div className="flex flex-col justify-center items-center text-black gap-y-3 px-4">
                <img
                  src="/assets/noevents.png"
                  alt="No events"
                  className="h-[95px] w-[95px]"
                />
                <p className="text-[15px] font-semibold">No New Suggestions</p>
                <p className="text-center text-[#404040] text-[13px]">
                  You’ve followed all available members! Check back soon as we
                  regularly update recommendations.
                </p>
              </div>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LeftSidebar;
